import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLang } from '../useLang';
import AnnouncementBar from './AnnouncementBar';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, t } = useLang();

  const navItems = [
    { label: t.nav.about, hash: '#about' },
    { label: t.nav.topics, hash: '#topics' },
    { label: t.nav.testimonials, hash: '#testimonials' },
    { label: t.nav.faqs, hash: '#faqs' },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goTo = (hash) => {
    setOpen(false);
    const home = `/${lang}`;
    if (location.pathname !== home) {
      navigate(home);
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 250);
    } else {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-cream/95 backdrop-blur shadow-sm' : 'bg-cream'}`}>
      <AnnouncementBar text={t.announcement} />
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between h-[86px]">
        {/* LEFT: Speaker name (hero of the brand) */}
        <Link to={`/${lang}`} className="flex flex-col leading-none group min-w-0">
          <span className="font-script text-3xl md:text-[2.4rem] transition-colors" style={{ color: 'var(--coral)' }}>
            Rachelle Koulibaly
          </span>
          <span className="font-head font-semibold text-[10px] md:text-[11px] tracking-[0.15em] md:tracking-[0.3em] uppercase mt-1.5 leading-snug" style={{ color: 'var(--navy)' }}>
            {t.header.role}
          </span>
        </Link>

        {/* CENTER: nav */}
        <nav className="hidden lg:flex items-center justify-center gap-8 flex-1">
          {navItems.map((n) => (
            <button key={n.hash} onClick={() => goTo(n.hash)}
              className="font-head text-[15px] font-medium text-navy/80 hover:text-coral transition-colors">
              {n.label}
            </button>
          ))}
        </nav>

        {/* RIGHT: language switch + CTA */}
        <div className="flex items-center gap-4 shrink-0">
          <LangSwitch lang={lang} />
          <button onClick={() => { setOpen(false); navigate(`/${lang}/book`); }}
            className="hidden lg:inline-flex btn-coral font-head font-semibold text-sm px-5 py-2.5 rounded-md">
            {t.header.book}
          </button>
          <button className="lg:hidden text-navy" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-cream border-t border-black/5 px-5 py-4 flex flex-col gap-3">
          {navItems.map((n) => (
            <button key={n.hash} onClick={() => goTo(n.hash)}
              className="font-head text-left text-navy/80 py-1.5">{n.label}</button>
          ))}
          <button onClick={() => { setOpen(false); navigate(`/${lang}/book`); }}
            className="btn-coral font-head font-semibold px-5 py-3 rounded-md mt-1">{t.header.book}</button>
        </div>
      )}
    </header>
  );
};

// Small EN | FR toggle that keeps the visitor on the same page
const LangSwitch = ({ lang }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const switchTo = (target) => {
    if (target === lang) return;
    const parts = location.pathname.split('/');
    parts[1] = target;
    navigate(parts.join('/') || `/${target}`);
  };

  const base = 'font-head font-semibold text-[13px] px-2 py-1 rounded transition-colors';
  return (
    <div className="hidden sm:flex items-center gap-1 rounded-md px-1 py-0.5" style={{ background: 'rgba(97,93,210,.10)' }}>
      {['en', 'fr'].map((l) => (
        <button key={l} onClick={() => switchTo(l)}
          className={base}
          style={l === lang
            ? { background: 'var(--navy)', color: '#fff' }
            : { color: 'var(--navy)' }}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

export default Header;
